// src/components/Login.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "../../api/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./Login.css";

const Login = () => {
  const [isActive, setIsActive] = useState(false); // toggle signup/login
  const [forgotStep, setForgotStep] = useState(0); // 0 = normal login, 1 = email/mobile, 2 = OTP, 3 = reset password
  const [loginId, setLoginId] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [signupData, setSignupData] = useState({ name: "", email: "", mobile: "", password: "", confirmPassword: "" });

  const navigate = useNavigate();

  // ---------------- LOGIN ----------------
  const handleLogin = async () => {
    try {
      const res = await ApiService.create("login", { login_id: loginId, password });
      setMessage(res.message);
      localStorage.setItem("token", res.token);
      localStorage.setItem("role", res.user.role); 
      if (res.user.role === "owner") {
        navigate("/admin/dashboard");
      } else {
        navigate("/");
      }
    } catch (err) {
      setMessage(err.response?.data?.message || "Login failed");
    }
  };



  // ---------------- FORGOT PASSWORD ----------------
  const handleSendOtp = async () => {
    try {
      const res = await ApiService.create("forgot-password", { login_id: loginId });
      setMessage(res.message);
      setForgotStep(2); // OTP step
    } catch (err) {
      setMessage(err.response?.data?.message || "Error sending OTP");
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const res = await ApiService.create("verify-otp", { login_id: loginId, otp });
      setMessage(res.message);
      setForgotStep(3); // Reset password step
    } catch (err) {
      setMessage(err.response?.data?.message || "Invalid OTP");
    }
  };

  const handleResetPassword = async () => {
    if (password !== confirmPassword) {
      setMessage("Passwords do not match");
      return;
    }
    try {
      const res = await ApiService.create("reset-password", {
        login_id: loginId,
        password,
        password_confirmation: confirmPassword
      });
      setMessage(res.message);
      setForgotStep(0); // back to normal login
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      setMessage(err.response?.data?.message || "Error resetting password");
    }
  };

  // ---------------- RENDER FORM INSIDE CONTAINER ----------------
  const renderForm = () => {
    // Forgot Password flow
    if (forgotStep > 0) {
      if (forgotStep === 1) {
        return (
          <>
            <h1>Forgot Password</h1>
            <input
              type="text"
              placeholder="Email or Mobile"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
            />
            <button type="button" onClick={handleSendOtp}>Send OTP</button>
          </>
        );
      } else if (forgotStep === 2) {
        return (
          <>
            <h1>Enter OTP</h1>
            <input
              type="text"
              placeholder="OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            <button type="button" onClick={handleVerifyOtp}>Verify OTP</button>
          </>
        );
      } else if (forgotStep === 3) {
        return (
          <>
            <h1>Reset Password</h1>
            <input
              type="password"
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button type="button" onClick={handleResetPassword}>Reset Password</button>
          </>
        );
      }
    }

    // Normal Login
    return (
      <>
        <h1>Sign In</h1>
        <input type="text" placeholder="Email or Mobile" value={loginId} onChange={(e) => setLoginId(e.target.value)} />
        <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <a href="#" onClick={() => { setForgotStep(1); setMessage(""); }}>Forget Your Password?</a>
        <button type="button" onClick={handleLogin}> Sign In </button>
      </>
    );
  };

  // ---------------- SIGNUP STATE ----------------
const [signupStep, setSignupStep] = useState(1); // 1 = Name/Mobile, 2 = Email, 3 = Address, 4 = Password
const [mobileVerified, setMobileVerified] = useState(false);
const [emailVerified, setEmailVerified] = useState(false);

// ---------------- SIGNUP HANDLERS ----------------

// Mobile verification
const handleVerifyMobile = () => {
  if (!isValidIndianMobile(signupData.mobile)) {
    toast.error("Enter a valid Indian mobile number", {
      icon: "❌",
    });
    return;
  }
  setMobileVerified(true);
  toast.success("Mobile verified!", {
    icon: "✅",
  });
};

// Email verification
const handleVerifyEmail = async () => {
  if (!isValidEmail(signupData.email)) {
    toast.error("Enter a valid email");
    return;
  }

  try {
    // Call API to send OTP
    await ApiService.create("signup/send-email-otp", { email: signupData.email });
    toast.success("OTP sent to email!");

    // Show OTP input
    setShowEmailOtpField(true);
  } catch (err) {
    toast.error(err.response?.data?.message || "Failed to send OTP");
  }
};





const handleNextStep = () => {
  if (signupStep === 1 && !mobileVerified) {
    setMessage("Verify mobile first");
    return;
  }
  if (signupStep === 2 && !emailVerified) {
    setMessage("Verify email first");
    return;
  }
  setSignupStep(signupStep + 1);
};

const handleSignup = async () => {
  try {
    const res = await ApiService.create("signup/complete", {
      ...signupData,
      password_confirmation: signupData.confirmPassword
    });
    setMessage(res.message);
    setIsActive(false); // go to login
  } catch (err) {
    setMessage(err.response?.data?.message || "Signup failed");
  }
};

// ---------------- SIGNUP FORM RENDER ----------------


const isValidIndianMobile = (number) => /^[6-9]\d{9}$/.test(number);

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const [emailOtp, setEmailOtp] = useState("");
const [showEmailOtpField, setShowEmailOtpField] = useState(false);

const renderSignupForm = () => {
  switch (signupStep) {
case 1:
  return (
    <>
      {/* NAME */}
      <input
        type="text"
        placeholder="Name"
        value={signupData.name}
        onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
      />

      {/* MOBILE FIELD */}
      <div className="verify-wrapper">
        <input
          type="text"
          className="verify-input"
          placeholder="Mobile (10 digits)"
          value={signupData.mobile}
          onChange={(e) => setSignupData({ ...signupData, mobile: e.target.value })}
        />
        <button
          type="button"
          className="verify-btn"
          onClick={handleVerifyMobile}
        >
          Verify
        </button>
      </div>

      {/* EMAIL FIELD */}
      <div className="verify-wrapper">
        <input
          type="email"
          className="verify-input"
          placeholder="Email"
          value={signupData.email}
          onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
        />
        <button
          type="button"
          className="verify-btn"
          onClick={handleVerifyEmail}
        >
          Verify
        </button>
      </div>

      {/* EMAIL OTP FIELD */}
      {showEmailOtpField && (
        <div className="verify-wrapper">
          <input
            type="text"
            className="verify-input"
            placeholder="Enter Email OTP"
            value={emailOtp}
            onChange={(e) => setEmailOtp(e.target.value)}
          />
          <button
            type="button"
            className="verify-btn"
            onClick={async () => {
              try {
                await ApiService.create("signup/verify-email-otp", {
                  email: signupData.email,
                  otp: emailOtp,
                });
                toast.success("Email verified successfully!");
                setEmailVerified(true);
                setShowEmailOtpField(false); // hide OTP after success
              } catch (err) {
                toast.error(err.response?.data?.message || "Invalid OTP");
              }
            }}
          >
            Verify
          </button>
        </div>
      )}

      <button type="button" onClick={handleNextStep}>Next</button>
    </>
  );

    case 2:
      return (
        <>
          <input
            type="text"
            placeholder="Address Line 1"
            value={signupData.address_line1 || ""}
            onChange={(e) => setSignupData({ ...signupData, address_line1: e.target.value })}
          />
          <input
            type="text"
            placeholder="Address Line 2"
            value={signupData.address_line2 || ""}
            onChange={(e) => setSignupData({ ...signupData, address_line2: e.target.value })}
          />
          <input
            type="text"
            placeholder="District"
            value={signupData.district || ""}
            onChange={(e) => setSignupData({ ...signupData, district: e.target.value })}
          />
          <input
            type="text"
            placeholder="State"
            value={signupData.state || ""}
            onChange={(e) => setSignupData({ ...signupData, state: e.target.value })}
          />
          <input
            type="text"
            placeholder="Country"
            value={signupData.country || ""}
            onChange={(e) => setSignupData({ ...signupData, country: e.target.value })}
          />
          <input
            type="text"
            placeholder="Postal Code"
            value={signupData.postal_code || ""}
            onChange={(e) => setSignupData({ ...signupData, postal_code: e.target.value })}
          />
          <button type="button" onClick={() => setSignupStep(4)}>Next</button>
        </>
      );
    case 4:
      return (
        <>
          <input
            type="password"
            placeholder="Password"
            value={signupData.password}
            onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
          />
          <input
            type="password"
            placeholder="Confirm Password"
            value={signupData.confirmPassword}
            onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
          />
          <button type="button" onClick={handleSignup}>Sign Up</button>
        </>
      );
    default:
      return null;
  }
};


  return (
    <div className="login-body">
      <div className={`container ${isActive ? "active" : ""}`} id="container">

        {/* ---------------- SIGNUP ---------------- */}
      <div className="form-container sign-up">
        <form onSubmit={(e) => e.preventDefault()}>
          <h1>Create Account</h1>
          {message && <p className="error-message">{message}</p>}
          {renderSignupForm()} {/* renders the multi-step form based on signupStep */}
          {}
        </form>
      </div>


        {/* ---------------- LOGIN / FORGOT PASSWORD ---------------- */}
        <div className="form-container sign-in">
          <form onSubmit={(e) => e.preventDefault()}>
            {renderForm()}
            {}
          </form>
        </div>

        {/* ---------------- TOGGLE PANEL ---------------- */}
        <div className="toggle-container">
          <div className="toggle">
            <div className="toggle-panel toggle-left">
              <h1>Welcome Back!</h1>
              <p>Enter your personal details to use all site features</p>
              <button className="btn" onClick={() => setIsActive(false)}>Sign In</button>
            </div>

            <div className="toggle-panel toggle-right">
              <h1>Hello, Friend!</h1>
              <p>Register with your personal details to use all site features</p>
              <button className="btn" onClick={() => setIsActive(true)}>Sign Up</button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer autoClose={3000} />

    </div>
  );
};

export default Login;
